import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Calendar, CheckCircle, XCircle, RefreshCw, ExternalLink, Unplug } from 'lucide-react';
import api from '../lib/api';

export default function GoogleCalendarIntegration() {
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['google-calendar-status'],
    queryFn: () => api.get('/integrations/google/status'),
  });

  const connectMutation = useMutation({
    mutationFn: () => api.get('/integrations/google/connect'),
    onSuccess: (data) => {
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    },
    onError: (err) => toast.error(err.message || 'Failed to connect'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => api.post('/integrations/google/disconnect'),
    onSuccess: () => {
      queryClient.invalidateQueries(['google-calendar-status']);
      toast.success('Google Calendar disconnected');
    },
    onError: (err) => toast.error(err.message || 'Failed to disconnect'),
  });

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.post('/integrations/google/sync');
      toast.success(`Synced ${result.meetings_synced} meetings`);
      queryClient.invalidateQueries(['google-calendar-status']);
    } catch (err) {
      toast.error(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  const connected = status?.connected;

  if (!connected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-8 h-8 text-muted-foreground" />
          <div>
            <p className="font-medium">Not connected</p>
            <p className="text-sm text-muted-foreground">
              Connect your Google Calendar to automatically sync meetings.
            </p>
          </div>
        </div>
        <Button
          onClick={() => connectMutation.mutate()}
          disabled={connectMutation.isPending}
          className="gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          {connectMutation.isPending ? 'Connecting...' : 'Connect Google Calendar'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-8 h-8 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">Google Calendar</p>
              <Badge variant="outline" className="gap-1 text-green-600 border-green-200 bg-green-50">
                <CheckCircle className="w-3 h-3" /> Connected
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {status.last_sync
                ? `Last synced: ${new Date(status.last_sync).toLocaleString()}`
                : 'Not synced yet'}
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (window.confirm('Disconnect Google Calendar? Your meetings will remain in PocketFiler but will no longer sync.')) {
              disconnectMutation.mutate();
            }
          }}
          disabled={disconnectMutation.isPending}
          className="gap-2 text-red-600 hover:text-red-700"
        >
          <Unplug className="w-4 h-4" />
          Disconnect
        </Button>
      </div>
    </div>
  );
}
